import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { css } from '@emotion/core';
import LocalInput from '../../../localization/LocalInput';
import { INPUT } from '../../../forms/styles/input';
import { Str } from '../../../localization/translationKeys';
import { SELECT } from '../../../forms/styles/select';
import LocalBtn from '../../../localization/LocalBtn';
import { btnStyle } from '../../../forms/styles/button';
import { COLORS } from '../../../globalStyle/colors';
import LocalDropdownOption from '../../../localization/LocalDropdownOption';
import { RaterLevel } from './models';
import * as R from 'ramda';
import { useFormik } from 'formik';
import { addRaterByEmailFormValidation } from './formValidations';
import { FLEX_COLUMN, FLEX_ADAPTIVE_MED } from '../../../layout/flexBox';
import { onMobileMedium } from '../../../globalStyle/mediaQueries';
import LocalDiv from '../../../localization/LocalDiv';
import { useMsgText } from '../../../localization/hooks';
import LocalSelect from '../../../localization/LocalSelect';
import { Requirement } from '../../models';
import { isNilOrEmpty } from '../../../language/module';
import { canAddRaterForLevel } from '../../module';
import { useAppState } from '../../../core/redux/hooks';


const styles = {
  root:                 css`
    ${onMobileMedium} {
      width: 100%;
    }
  `,
  input:                css`
    margin: 4px;
    min-width: 192px;
    font-size: 12px;
    border-color: ${COLORS.WHITE};

    ${onMobileMedium} {
      width: 100%;
    }
  `,
  essentialFields:      css`
    flex-wrap: wrap;
  `,
  dropdownBtnContainer: {
    root:         css`
      flex-basis: min-content;
    `,
    dropdown:     css`
      margin: 4px;
      width: 192px;
      font-size: 12px;
      border-color: ${COLORS.WHITE};

      ${onMobileMedium} {
        width: 100%;
      }
    `,
    btnContainer: css`
      margin: 4px;
      ${onMobileMedium} {
        width: 100%;
      }
    `,
    button:       css`
      ${onMobileMedium} {
        width: 100%;
      }
    `,
  },
};


type Props = {
  className?: string;
  raterLevels: RaterLevel[];
  requirements?: Requirement[];
  addRater: (levelId: number, email: string, firstName: string, lastName: string) => void;
  addingRater?: boolean;
  setEmailAddress: (email: string) => void;
  setErrorMsg: (errorMsg?: string) => void;
  showNameFields: boolean;
  requireNamesOnAdd: boolean;
  maximumsRequired: boolean;
  approver: boolean;
}


const renderRaterLevelOptions = (levels: RaterLevel[]) => R.map(
  level => (
    <LocalDropdownOption
      key={level.surveyLevelId}
      value={level.surveyLevelId}
      labelKey={''}
      labelText={level.fullName}
    />
  ),
  levels,
);


/**
 * AddRaterForm: a component that enables users to add raters by email address (and, where required, their full name).
 *
 * PureComponent: true
 * Redux Connected: false
 */
const AddRaterForm: React.FC<Props> = props => {
  const invalidEmail = useMsgText(Str.InvalidEmail);
  const includeName  = useMsgText(Str.NameRequired);

  const relationshipMsg    = useMsgText(Str.Relationship);
  const maximumRatersAdded = useMsgText(Str.MaxRatersMsg);

  const hideLevelSelect = useMemo(() => props.raterLevels.length === 1, [props.raterLevels]);

  const { handleSubmit, handleChange, resetForm, isValid, errors, values, setErrors } = useFormik({
    initialValues:      {
      firstName: '',
      lastName:  '',
      email:     '',
      levelId:   hideLevelSelect ? `${props.raterLevels[0]?.surveyLevelId}` : '',
    },
    onSubmit:           (values) => {
      if (!!props.requirements) {
        const requirement = props.requirements.find(req => req.surveyLevelId === +values.levelId!);

        // If we have a requirement for the selected level, make sure we're allowed to add a rater for it.
        if (!!requirement && !canAddRaterForLevel(props.maximumsRequired, requirement)) {
          setErrors({ email: maximumRatersAdded.replace('{levelName}', requirement.fullName) });
          return;
        }
      }

      props.addRater(+values.levelId!, values.email, values.firstName, values.lastName);
      props.setEmailAddress(values.email);

      resetForm();
      setErrors({});
    },
    validationSchema:   addRaterByEmailFormValidation(invalidEmail, includeName, props.requireNamesOnAdd),
    validateOnChange:   false,
    enableReinitialize: true,
  });

  const errorMsg = errors.email || errors.firstName || errors.lastName || errors.levelId;
  
  const dir = useAppState(state => state.locale.textDirection);
  
  useEffect(
    () => props.setErrorMsg(errorMsg),
    [errorMsg, props, setErrors],
  );

  return (
    <div css={[FLEX_COLUMN, styles.root]} className={props.className}>
      <fieldset
        css={css`border: 0 none; margin: 0; padding: 0;`}
        disabled={props.addingRater}
      >
        <form onSubmit={handleSubmit}>
          {
            props.showNameFields && (
              <LocalDiv css={[FLEX_ADAPTIVE_MED]}>
                <LocalInput
                  placeholderTranslation={Str.FirstName}
                  name='firstName'
                  type='text'
                  value={values.firstName}
                  css={[
                    INPUT(),
                    styles.input,
                    css`border-color: ${
                      !isValid && props.requireNamesOnAdd && isNilOrEmpty(values.firstName)
                      ? COLORS.RED
                      : COLORS.WHITE};`,
                  ]}
                  onChange={handleChange}
                />
                <LocalInput
                  placeholderTranslation={Str.LastName}
                  name='lastName'
                  type='text'
                  value={values.lastName}
                  css={[
                    INPUT(),
                    styles.input,
                    css`border-color: ${
                      !isValid && props.requireNamesOnAdd && isNilOrEmpty(values.lastName)
                      ? COLORS.RED
                      : COLORS.WHITE};`,
                  ]}
                  onChange={handleChange}
                />
              </LocalDiv>
            )
          }
          <LocalDiv css={[FLEX_ADAPTIVE_MED, styles.essentialFields]}>
            <LocalInput
              placeholderTranslation={Str.Email}
              name='email'
              type='text'
              value={values.email}
              css={[
                INPUT(),
                styles.input,
                css`border-color: ${
                  !isValid && !/^[ ]*([^@\s]+)@((?:[-a-z0-9]+\.)+[a-z])[ ]*$/i.test(values.email)
                  ? COLORS.RED
                  : COLORS.WHITE};`,
               ]}
              onChange={handleChange}
            />
            {
              !hideLevelSelect && (
                <LocalSelect
                  css={[SELECT, styles.dropdownBtnContainer.dropdown]}
                  name='levelId'
                  value={values.levelId}
                  onChange={handleChange}
                  dir={dir}
                  required={true}
                >
                  {
                    isNilOrEmpty(values.levelId) && (
                      <option
                        dir={dir}
                        value=''
                        hidden={true}
                        label={relationshipMsg}
                      >
                        {relationshipMsg}
                      </option>
                    )
                  }
                  {renderRaterLevelOptions(props.raterLevels)}
                </LocalSelect>
              )
            }
            <LocalDiv css={styles.dropdownBtnContainer.btnContainer}>
              <LocalBtn
                str={props.approver ? Str.AddApproveRater : Str.AddRater}
                type='submit'
                css={[btnStyle.primary(COLORS.DARK_GREY), styles.dropdownBtnContainer.button]}
              />
            </LocalDiv>
          </LocalDiv>
        </form>
      </fieldset>
    </div>
  );
};

export default AddRaterForm;
